import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { body14Styles, smallcapsBold13Styles } from "@constants/typography"
import { getShopURLForSeriesDeviceType } from "@utils/urls"
import { Link } from "gatsby"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  ${smallcapsBold13Styles}
  display: flex;
  height: 40px;
  margin-bottom: 10px;
  padding: 4px 12px 0;
  align-items: center;
  background-color: transparent;
`

const Series = styled(Link)`
  ${body14Styles}
  display: inline-flex;
  padding: 0 12px;
  align-items: flex-start;
  color: ${colors.mediumDarkGray};
  line-height: 32px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: transparent;

  &:hover {
    transition: background-color ${animation.quick} ${cubicBezier};
    background-color: ${colors.dividerLineGray};
  }
`

const MoreSeriesText = styled.span`
  ${body14Styles}
  height: 32px;
  color: ${colors.mediumGray};
  transition:
    color ${animation.medium} ${cubicBezier},
    border ${animation.medium} ${cubicBezier};
  border-bottom: solid 1px ${colors.lightGray};
`

const MoreSeries = styled.button<{ $hideButton: boolean }>`
  display: flex;
  width: auto;
  margin: 16px 0 0;
  padding: 0 0 0 12px;
  background: none;
  border: none;

  ${({ $hideButton }) =>
    $hideButton &&
    `
      width: 0;
      height: 0;
      margin: 0;
      visibility: hidden;
  `}

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;

    ${MoreSeriesText} {
      color: ${colors.black};
      transition:
        color ${animation.quick} ${cubicBezier},
        border ${animation.quick} ${cubicBezier};
      border-bottom: solid 1px ${colors.black};
    }
  }
`

interface SeriesListProps {
  allSeries: {
    featuredSeries: SeriesProps[]
    series: SeriesProps[]
  }
  deviceType: string
}

interface SeriesProps {
  name: string
  slug: string
}

const SeriesList: React.FC<SeriesListProps> = ({ allSeries, deviceType }) => {
  const [showMoreSeries, setShowMoreSeries] = React.useState<boolean>(false)

  const handleShowMoreSeries = () => {
    setShowMoreSeries(true)
  }

  const renderedSeries = !showMoreSeries
    ? allSeries.featuredSeries
    : [...allSeries.featuredSeries, ...allSeries.series]

  return (
    <Container>
      <Header>Shop UAG Series</Header>
      {renderedSeries.map((series) => (
        <Series
          key={`${deviceType}-${series.slug}`}
          to={getShopURLForSeriesDeviceType(series.slug, deviceType)}
        >
          {series.name}
        </Series>
      ))}
      {allSeries.series.length && !showMoreSeries && (
        <MoreSeries $hideButton={showMoreSeries} onClick={handleShowMoreSeries}>
          <MoreSeriesText>View More Series</MoreSeriesText>
        </MoreSeries>
      )}
    </Container>
  )
}

export default SeriesList
